import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  Space,
  Tag,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import { boreholeStatusData, damStatusData } from "../../../data";
import dayjs from "dayjs";
import { normalizeDateToLocalYear } from "../../../utils";

const { TextArea } = Input;
const BoreholeAdd = () => {
  const urlPwrSrc = "power_source/fetch_powersources";
  const urlWtrUse = "water_use/fetch_wateruses";
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlAgency = "api/v2/agency/get";

  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const { counties, subCounties, locations, subLocations, subsidySources } =
    useSelector((state) => state.fetch);
  const { activeObjBorehole, dataWtrUse, dataPwrSrc, dataPathway, dataAgency, dataConstituency,
    dataWard, } =
    useSelector((state) => ({
      activeObjBorehole: state.obj.activeObj["borehole"],
      dataPwrSrc: state.fetch.data[urlPwrSrc] || [],
      dataWtrUse: state.fetch.data[urlWtrUse] || [],
      dataPathway: state.fetch.data[urlPathway] || [],
      dataAgency: state.fetch.data[urlAgency] || [],
      dataWard: state.fetch.data[urlWard] || [],
      dataConstituency: state.fetch.data[urlConstituency] || [],
    }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/boreholes");
  }

  const [countyName, setCountyName] = useState(
    activeObjBorehole?.countyName ?? ""
  );

  const onFinish = async (data) => {
    data.bhId = activeObjBorehole?.bhId ? activeObjBorehole?.bhId : null;

    data.bhSourceFundsId = Array.isArray(data?.bhSourceFundsId)
      ? data?.bhSourceFundsId.join(",")
      : data?.bhSourceFundsId;

    data.bhPathwayId = Array.isArray(data?.bhPathwayId)
      ? data?.bhPathwayId.join(",")
      : data?.bhPathwayId;

    data.bhStartDate = normalizeDateToLocalYear(data?.bhStartDate);
    data.bhEndDate = normalizeDateToLocalYear(data?.bhEndDate);
    const res = await dispatch(
      save({
        ...data,
        url: "borehole/save_borehole",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const [countyId, setCountyId] = useState(activeObjBorehole?.bhCountyId ?? "");
  const [constituencyId, setConstituencyId] = useState(activeObjBorehole?.bhConstId ?? "");
  const [wardId, setWardId] = useState(activeObjBorehole?.bhWardId ?? "");
  const [subCountyId, setSubCountyId] = useState(activeObjBorehole?.bhSubCountyId ?? "");
  const [locationId, setLocationId] = useState(activeObjBorehole?.bhLocationId ?? "");
  const [subLocationId, setSubLocationId] = useState(activeObjBorehole?.bhSubLocationId ?? "");

  const handleCountyChange = async (value) => {
    setCountyId(value);
    setConstituencyId("");
    setSubCountyId("");
    setLocationId("");
    setSubLocationId("");
    setWardId("");
  
    form.setFieldsValue({
      bhCountyId: value,
      bhConstId: "",
      bhSubCountyId: "",
      bhLocationId: "",
      bhSubLocationId: "",
      bhWardId: "",
    });
  
    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(fetchData({ url: urlConstituency, constCountyId: value }));
  };
  
  const handleConstituencyChange = async (value) => {
    setConstituencyId(value);
    setWardId("");
    form.setFieldsValue({ bhConstId: value, bhWardId: "" });
    await dispatch(fetchData({ url: urlWard, wdConstId: value }));
  };
  
  const handleSubCountyChange = async (value) => {
    setSubCountyId(value);
    setLocationId("");
    setSubLocationId("");
  
    form.setFieldsValue({
      bhSubCountyId: value,
      bhLocationId: "",
      bhSubLocationId: "",
    });
  
    await dispatch(fetchLocation({ locSubId: value }));
  };
  
  const handleLocationChange = async (value) => {
    setLocationId(value);
    setSubLocationId("");
  
    form.setFieldsValue({
      bhLocationId: value,
      bhSubLocationId: "",
    });
  
    await dispatch(fetchSubLocation({ slocLocId: value }));
  };
  
  const handleSubLocationChange = (value) => {
    setSubLocationId(value);
    form.setFieldsValue({ bhSubLocationId: value });
  };
  
  const handleWardChange = (value) => {
    setWardId(value);
    form.setFieldsValue({ bhWardId: value });
  };
  


  const handleDatesChange = (_, allValues) => {
    const { bhStartDate, bhEndDate } = allValues;
    if (bhStartDate && bhEndDate) {
      const diff = dayjs(bhEndDate)?.diff(dayjs(bhStartDate), "day");
      form.setFieldsValue({
        bhTimeFrame: `${diff} day${diff !== 1 ? "s" : ""}`,
      });
    } else {
      form.setFieldsValue({ bhTimeFrame: "" });
    }
  };

  useEffect(() => {
    dispatch(
      fetchData({
        url: urlPwrSrc,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlWtrUse,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlAgency,
        limit: null,
        start: 0,
      })
    );
    dispatch(fetchSubsidySource());
  }, []);

  const initialFormValues = {
    ...activeObjBorehole,
    bhStartDate: activeObjBorehole?.bhStartDate
      ? dayjs(new Date(activeObjBorehole?.bhStartDate))
      : null,
    bhEndDate: activeObjBorehole?.bhEndDate
      ? dayjs(new Date(activeObjBorehole?.bhEndDate))
      : null,
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjBorehole,
      bhStartDate: activeObjBorehole?.bhStartDate
        ? dayjs(new Date(activeObjBorehole?.bhStartDate))
        : null,
      bhEndDate: activeObjBorehole?.bhEndDate
        ? dayjs(new Date(activeObjBorehole?.bhEndDate))
        : null,
    });
    if (activeObjBorehole?.bhSourceFundsId) {
      form.setFieldsValue({
        bhSourceFundsId: activeObjBorehole?.bhSourceFundsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjBorehole?.bhPathwayId) {
      form.setFieldsValue({
        bhPathwayId: activeObjBorehole?.bhPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjBorehole, form]);

  useEffect(() => {
    if (activeObjBorehole?.bhId) {
      dispatch(fetchSubCounties({ subCountyId: activeObjBorehole?.bhCountyId }));
      dispatch(fetchData({ url: urlConstituency, constCountyId: activeObjBorehole?.bhCountyId }));
      dispatch(fetchData({ url: urlWard, wdConstId: activeObjBorehole?.bhConstId }));
      dispatch(fetchLocation({ locSubId: activeObjBorehole?.bhSubCountyId }));
      dispatch(fetchSubLocation({ slocLocId: activeObjBorehole?.bhLocationId }));
    }
  }, [activeObjBorehole, form]);
  

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "Boreholes", path: "/rob/boreholes" },
          { title: "Boreholes Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">Borehole Information</div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
            onValuesChange={handleDatesChange}
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="bhName"
                label={"Name of Borehole"}
                rules={[
                  {
                    required: true,
                    message: "Please add name of borehole",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              
              <Form.Item
                name="bhCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {countyId && (
                <>
                  <Form.Item
                    name="bhConstId"
                    label="Constituency"
                    rules={[{ required: true, message: "Please select constituency" }]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label?.toLowerCase()?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="bhSubCountyId"
                    label="Subcounty"
                    rules={[{ required: true, message: "Please select subcounty" }]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label?.toLowerCase()?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {subCountyId && (
                <Form.Item
                  name="bhLocationId"
                  label="Location"
                  rules={[{ required: true, message: "Please select location" }]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase()?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {locationId && (
                <Form.Item
                  name="bhSubLocationId"
                  label="Sublocation"
                  rules={[{ required: true, message: "Please select sublocation" }]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase()?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {constituencyId && (
                <Form.Item
                  name="bhWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.wdId,
                      label: w?.wdWard,
                    }))}
                    onChange={handleWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label?.toLowerCase()?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}


              <Form.Item
                name="bhLat"
                label={"GPS Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="bhLong"
                label={"GPS Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Ksh. e.g 500"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="bhCost"
                label={"Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add cost",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="bhPowerSourceId"
                className=""
                label={"Power Source"}
                rules={[
                  {
                    required: true,
                    message: "Please select power source",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataPwrSrc?.length > 0 &&
                    dataPwrSrc?.map((item) => ({
                      value: item?.psId,
                      label: item?.psType,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="bhTotalDepth"
                label={"Total Depth (M)"}
                rules={[
                  {
                    required: true,
                    message: "Please add total depth",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>M</span>}
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="bhYield"
                label={"Yield (M³/Hr)"}
                rules={[
                  {
                    required: true,
                    message: "Please add yield (M³/Hr)",
                  },
                ]}
              >
                <InputNumber 
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="bhPumpRating"
                label={"Pump Rating"}
                rules={[
                  {
                    required: true,
                    message: "Please add rating",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

             
              <Form.Item
                name="bhWaterQuality"
                label={"Water Quality"}
                rules={[
                  {
                    required: true,
                    message: "Please add Water Quality",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>

              <Form.Item
                name="bhEstBeneficiaries"
                label={"Estimated Households"}
                rules={[
                  {
                    required: true,
                    message: "Please add estimated households",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="bhWaterUseId"
                className=""
                label={"Purpose/Water Use"}
                rules={[
                  {
                    required: true,
                    message: "Please select purpose/water use",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataWtrUse?.length > 0 &&
                    dataWtrUse?.map((item) => ({
                      value: item?.wuId,
                      label: item?.wuType,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name="bhAcreage"
                label={"Acreage"}
                rules={[
                  {
                    required: true,
                    message: "Please add Acreage",
                  },
                ]}
              >
                <InputNumber
                  suffix="Acres"
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                name="bhLivestockEst"
                label={"Livestock Estimate"}
                rules={[
                  {
                    required: true,
                    message: "Please add livestock estimate",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="bhStatus"
                className=""
                label={"Borehole Status"}
                rules={[
                  {
                    required: true,
                    message: "Please select borehole  status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    boreholeStatusData?.length > 0 &&
                    boreholeStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                label="Source of Funding"
                name="bhSourceFundsId"
                rules={[
                  { required: true, message: "Source of Funding is required" },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following sources
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {subsidySources?.map((item) => (
                    <Select.Option
                      key={item?.subsidySourceId}
                      value={item?.subsidySourceId}
                    >
                      {item?.subsidySourceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Start Date"
                name="bhStartDate"
                rules={[
                  {
                    required: true,
                    message: "Start date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="End Date"
                name="bhEndDate"
                rules={[
                  {
                    required: true,
                    message: "End date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Time Frame"
                name="bhTimeFrame"
                rules={[
                  {
                    required: true,
                    message: "Time Frame date is required",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                label={"Pathways"}
                name="bhPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="bhAgencyId"
                className=""
                label={"Implementing Entity/Department"}
                rules={[
                  {
                    required: true,
                    message: "Please select Implementing Entity/Department",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataAgency?.length > 0 &&
                    dataAgency?.map((item) => ({
                      value: item?.agencyId,
                      label: item?.agencyName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="bhManagingAgency"
                label={"Managing Entity"}
                rules={[
                  {
                    required: true,
                    message: "Please add Managing Entity",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
            </div>
             <Form.Item
                name="bmAuxiliaryWorks"
                label={"Auxiliary Works"}
                rules={[
                  {
                    required: true,
                    message: "Please add auxiliary works",
                  },
                ]}
              >
                <TextArea   rows={5} className="text_area" />
              </Form.Item>
            <Form.Item
              name="bhRemarks"
              label={"Remarks"}
              rules={[
                {
                  required: true,
                  message: "Please add remarks",
                },
              ]}
            >
              <TextArea
                placeholder="Description..."
                rows={5}
                className="text_area"
              />
            </Form.Item>
            
            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjBorehole?.bhId ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default BoreholeAdd;
